import { ShieldCheck, ShieldAlert, ShieldQuestion } from "lucide-react";

import { Tooltip } from "../../../components/ui/Tooltip";

/**
 * Snapshot of the MF VAT-whitelist check of the contractor, taken by the backend when
 * the invoice was submitted. Present in API responses only for internal (GK ELEKTIM)
 * users — the subcontractor never receives this field, so the badge simply never
 * renders on their side.
 */
export interface WhitelistCheck {
  status: "found" | "not_found" | "no_nip" | "unavailable";
  vat_status?: string;
  account_assigned?: boolean | null;
  account_number?: string;
  request_id?: string;
  checked_at?: string;
}

type Tone = "ok" | "warn" | "error" | "muted";

function verdict(c: WhitelistCheck): { tone: Tone; label: string; detail: string } {
  if (c.status === "unavailable")
    return {
      tone: "muted",
      label: "Biała lista: brak odpowiedzi",
      detail: "Rejestr MF nie odpowiadał przy składaniu faktury — weryfikacja nie została wykonana.",
    };
  if (c.status === "no_nip")
    return {
      tone: "muted",
      label: "Biała lista: brak NIP",
      detail: "Karta podwykonawcy nie ma poprawnego NIP — nie było czego zweryfikować.",
    };
  if (c.status === "not_found")
    return {
      tone: "error",
      label: "Brak na białej liście",
      detail: "Podwykonawcy nie ma w wykazie podatników VAT (biała lista MF).",
    };
  if (c.account_assigned === false)
    return {
      tone: "error",
      label: "Rachunek spoza białej listy",
      detail: `Rachunek ${c.account_number || "z karty podwykonawcy"} NIE figuruje na białej liście tego podmiotu. Przelew powyżej 15 tys. zł na ten rachunek grozi sankcjami podatkowymi.`,
    };
  if (c.vat_status && c.vat_status !== "Czynny")
    return {
      tone: "warn",
      label: `Biała lista: VAT ${c.vat_status.toLowerCase()}`,
      detail: "Podmiot figuruje w wykazie, ale nie jest czynnym podatnikiem VAT.",
    };
  return {
    tone: "ok",
    label: "Biała lista: OK",
    detail:
      c.account_assigned === true
        ? "Czynny podatnik VAT; rachunek z karty podwykonawcy figuruje na białej liście."
        : "Czynny podatnik VAT. Rachunku nie zweryfikowano (brak numeru na karcie podwykonawcy).",
  };
}

const TONE_STYLES: Record<Tone, { Icon: typeof ShieldCheck; cls: string }> = {
  ok: { Icon: ShieldCheck, cls: "text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800" },
  warn: { Icon: ShieldAlert, cls: "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800" },
  error: { Icon: ShieldAlert, cls: "text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800" },
  muted: { Icon: ShieldQuestion, cls: "text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700" },
};

/**
 * Whitelist verdict pill shown next to a contractor invoice for GK users only.
 * Renders nothing when the check is absent (contractor viewing, or check still queued).
 */
export function WhitelistBadge({ check }: { check?: WhitelistCheck | null }) {
  if (!check) return null;
  const { tone, label, detail } = verdict(check);
  const { Icon, cls } = TONE_STYLES[tone];
  const checkedAt = check.checked_at
    ? `\nSprawdzono: ${new Date(check.checked_at).toLocaleString("pl-PL")}`
    : "";
  const requestId = check.request_id ? `\nId zapytania MF: ${check.request_id}` : "";
  return (
    <Tooltip content={`${detail}${checkedAt}${requestId}`}>
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium cursor-help align-middle ${cls}`}
        aria-label="Weryfikacja na białej liście VAT"
      >
        <Icon className="w-3.5 h-3.5" />
        {label}
      </span>
    </Tooltip>
  );
}
